import { Add } from "@mui/icons-material";
import { Fab, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { parseAsString } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlProjectUser } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import UserBadge from "~/modules/user/components/UserBadge";
import SearchInput from "~/shared/components/SearchInput";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import useRequireCurrentProject from "../../hooks/useRequireCurrentProject";
import useProjectUserDelete from "./api/useProjectUserDelete";
import useProjectUserGetAllWithAccessToProject from "./api/useProjectUserGetAllWithAccessToProject";

export default function ProjectUserListScreen() {
    const { t } = useTranslation("user");
    const { project } = useRequireCurrentProject();

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const params = useMemo(
        () => ({
            projectId: project.id,
            q: debouncedQ || undefined,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [project, debouncedQ, queryState],
    );

    const { data, loading, error, totalRows } = useProjectUserGetAllWithAccessToProject(params);

    const { deleteProjectUser, loading: deleteLoading } = useProjectUserDelete();
    const handleDelete = (userId: string) => deleteProjectUser(project.id, userId);

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: GridColDef[] = [
        {
            field: "user",
            headerName: t("userListScreen.colName"),
            renderCell: (params: GridRenderCellParams<GqlProjectUser>) => {
                const user = params.row.user;
                return (
                    <UserBadge
                        names={[user.firstName, user.lastName]}
                        profilePicture={user.profilePictureMedia?.url}
                        showName={true}
                    />
                );
            },
            flex: 1,
        },
        {
            field: "email",
            headerName: t("userListScreen.colEmail"),
            valueGetter: (_, row) => row.user?.email,
            flex: 1,
        },
        {
            field: "phone",
            headerName: t("userListScreen.colPhone"),
            valueGetter: (_, row) => row.user?.phone,
            flex: 1,
        },
        {
            field: "actions",
            headerName: "",
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "right",
            renderCell: (params: GridRenderCellParams<GqlProjectUser>) => (
                <GaiaDeleteButton onDelete={() => handleDelete(params.row.userId)} isLoading={deleteLoading} />
            ),
        },
    ];

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={t("userListScreen.title")}
                actions={
                    <Tooltip title={t("userListScreen.tooltipNewUser")}>
                        <Fab component={Link} to="new" color="primary">
                            <Add />
                        </Fab>
                    </Tooltip>
                }
            >
                <SearchInput
                    label={t("userListScreen.filterSearch")}
                    value={queryState.q}
                    onChange={value => handleParameterChange("q", value)}
                />

                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={totalRows || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
